import {
  availableVariablesAtom,
  disableVariablesAutocompleteAtom,
  variableValidationAtom,
} from "@/components/TemplateEditor/store";
import { cn } from "@/lib";
import { useAtomValue } from "jotai";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { getFlattenedVariables } from "../../utils/getFlattenedVariables";
import { isValidVariableName } from "../../utils/validateVariableName";
import { VariableAutocomplete } from "./VariableAutocomplete";

export const MAX_VARIABLE_LENGTH = 50;
export const MAX_DISPLAY_LENGTH = 24;

export interface VariableColors {
  bgColor: string;
  borderColor: string;
  iconColor: string;
  textColor: string;
}

export interface VariableChipBaseProps {
  /** The variable name/id */
  variableId: string;
  /** Whether the variable is currently invalid */
  isInvalid: boolean;
  /** Called when attributes should be updated */
  onUpdateAttributes: (attrs: { id: string; isInvalid: boolean }) => void;
  /** Called when the node should be deleted */
  onDelete: () => void;
  /** Icon component to render */
  icon: React.ReactNode;
  /** Optional value to display (e.g., for variables with known values) */
  value?: string;
  /** Whether to use single-click (true) or double-click (false) to edit */
  singleClickToEdit?: boolean;
  /** Additional class names for the outer span */
  className?: string;
  /** Override text color (e.g., for button context) */
  textColorOverride?: string;
  /** Custom color getter function (kept for API compatibility, colors handled by CSS) */
  getColors?: (isInvalid: boolean, hasValue: boolean) => VariableColors;
  /** Whether the chip is read-only (prevents editing) */
  readOnly?: boolean;
  /** Formatting styles derived from TipTap marks (bold, italic, underline, strikethrough) */
  formattingStyle?: React.CSSProperties;
  /** Whether the chip is within the current text selection */
  isSelected?: boolean;
  /** Called when the chip is clicked for selection (not editing) */
  onSelect?: () => void;
  /** Called after editing is committed (suggestion selected or blur confirmed) to restore editor focus */
  onCommit?: () => void;
  /** Whether this variable chip is inside a list node with a loop configured */
  isInsideLoop?: boolean;
}

export const VariableChipBase: React.FC<VariableChipBaseProps> = ({
  variableId,
  isInvalid,
  onUpdateAttributes,
  onDelete,
  icon,
  value,
  singleClickToEdit = false,
  className,
  textColorOverride,
  getColors: _getColors,
  readOnly = false,
  formattingStyle,
  isSelected = false,
  onSelect,
  onCommit,
  isInsideLoop = false,
}) => {
  void _getColors; // Colors handled by CSS, prop kept for API compatibility
  const [isEditing, setIsEditing] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const editableRef = useRef<HTMLSpanElement>(null);
  const chipRef = useRef<HTMLSpanElement>(null);
  const variableValidation = useAtomValue(variableValidationAtom);
  const availableVariables = useAtomValue(availableVariablesAtom);
  const disableAutocomplete = useAtomValue(disableVariablesAutocompleteAtom);

  // Get flattened list of variable suggestions
  const allSuggestions = useMemo(() => {
    const loopVars = isInsideLoop ? ["$.item", "$.index"] : [];

    if (
      disableAutocomplete ||
      !availableVariables ||
      Object.keys(availableVariables).length === 0
    ) {
      return loopVars;
    }
    return [...loopVars, ...getFlattenedVariables(availableVariables)];
  }, [availableVariables, disableAutocomplete, isInsideLoop]);

  // Filter suggestions based on current query
  const filteredSuggestions = useMemo(() => {
    if (allSuggestions.length === 0) return [];
    if (!query) return allSuggestions;
    return allSuggestions.filter((item) => item.toLowerCase().includes(query.toLowerCase()));
  }, [allSuggestions, query]);

  // Show autocomplete when editing and have suggestions
  const showAutocomplete = isEditing && filteredSuggestions.length > 0;

  // Auto-enter edit mode if id is empty (newly inserted variable)
  useEffect(() => {
    // Don't auto-enter edit mode in readonly mode
    if (readOnly) return;
    if (variableId === "" && !isEditing) {
      setIsEditing(true);
      setQuery("");
      setSelectedIndex(0);
    }
  }, [variableId, isEditing, readOnly]);

  // Validate variable against custom validator or available list on mount/change
  useEffect(() => {
    if (!variableId || isEditing) return;

    let isValid = true;
    const context = { isInsideLoop };

    if (variableValidation?.validate) {
      isValid = variableValidation.validate(variableId, context);
    } else if (allSuggestions.length > 0) {
      isValid =
        allSuggestions.includes(variableId) ||
        (isInsideLoop && variableId.startsWith("$.item.") && variableId.length > 7);
    }

    if (!isValid && !isInvalid) {
      queueMicrotask(() => {
        onUpdateAttributes({ id: variableId, isInvalid: true });
      });
    } else if (isValid && isInvalid) {
      queueMicrotask(() => {
        onUpdateAttributes({ id: variableId, isInvalid: false });
      });
    }
  }, [
    variableId,
    allSuggestions,
    isInvalid,
    isEditing,
    onUpdateAttributes,
    variableValidation,
    isInsideLoop,
  ]);

  // Focus and place cursor at end when entering edit mode
  useEffect(() => {
    if (isEditing && editableRef.current) {
      const el = editableRef.current;
      // Set initial content (variableId) when entering edit mode
      el.textContent = variableId;
      el.focus();
      // Use requestAnimationFrame to ensure cursor placement happens after DOM update
      requestAnimationFrame(() => {
        // Check if element is still connected to the DOM before manipulating selection
        // This prevents "addRange(): The given range isn't in document" errors
        if (el && el.isConnected) {
          const range = document.createRange();
          range.selectNodeContents(el);
          range.collapse(false); // Collapse to end
          const selection = window.getSelection();
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
      });
    }
  }, [isEditing, variableId]);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    // Read directly from DOM instead of React state to avoid cursor issues
    const trimmedValue = (editableRef.current?.textContent || "").trim();

    // If empty, delete the node
    if (trimmedValue === "") {
      onDelete();
      return;
    }

    // Validate the variable name
    let isValid = true;
    let customValidationFailed = false;

    const context = { isInsideLoop };

    if (variableValidation?.overrideFormatValidation) {
      if (variableValidation.validate) {
        isValid = variableValidation.validate(trimmedValue, context);
        if (!isValid) customValidationFailed = true;
      }
    } else {
      // Format validation first
      isValid = isValidVariableName(trimmedValue);

      // Custom validation only if format passes
      if (isValid && variableValidation?.validate) {
        isValid = variableValidation.validate(trimmedValue, context);
        if (!isValid) customValidationFailed = true;
      }
    }

    // List check — skip when a custom validator is provided
    if (isValid && allSuggestions.length > 0 && !variableValidation?.validate) {
      isValid = allSuggestions.includes(trimmedValue);
    }

    if (!isValid) {
      const onInvalid = variableValidation?.onInvalid ?? "mark";

      // Only show custom invalidMessage when the custom validator failed
      if (customValidationFailed && variableValidation?.invalidMessage) {
        const message =
          typeof variableValidation.invalidMessage === "function"
            ? variableValidation.invalidMessage(trimmedValue)
            : variableValidation.invalidMessage;
        toast.error(message);
      }

      if (onInvalid === "remove") {
        onDelete();
        return;
      }

      onUpdateAttributes({
        id: trimmedValue,
        isInvalid: true,
      });
      return;
    }

    // Valid variable
    onUpdateAttributes({
      id: trimmedValue,
      isInvalid: false,
    });
    onCommit?.();
  }, [onDelete, onUpdateAttributes, variableValidation, allSuggestions, onCommit, isInsideLoop]);

  // Handle selecting an item from autocomplete
  const handleSelectSuggestion = useCallback(
    (item: string) => {
      if (item === "$.item" && editableRef.current) {
        const expanded = "$.item.";
        editableRef.current.textContent = expanded;
        setQuery(expanded);
        setSelectedIndex(0);
        // Place caret at the end of "$.item."
        requestAnimationFrame(() => {
          const el = editableRef.current;
          if (el?.isConnected) {
            el.focus();
            const range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
            const selection = window.getSelection();
            selection?.removeAllRanges();
            selection?.addRange(range);
          }
        });
        return;
      }

      // Set the value in the editable span
      if (editableRef.current) {
        editableRef.current.textContent = item;
      }
      setQuery("");
      setIsEditing(false);
      // Update attributes with the selected variable
      onUpdateAttributes({
        id: item,
        isInvalid: false,
      });
      // Restore focus to the editor after exiting edit mode
      onCommit?.();
    },
    [onUpdateAttributes, onCommit]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLSpanElement>) => {
      // Block formatting shortcuts (Cmd/Ctrl+B, I, U, etc.) from reaching TipTap
      // while editing the variable name. Allow standard text-editing shortcuts through.
      if (e.metaKey || e.ctrlKey) {
        const key = e.key.toLowerCase();
        const allowedKeys = new Set(["a", "c", "v", "x", "z"]);
        if (!allowedKeys.has(key)) {
          e.stopPropagation();
          e.preventDefault();
          return;
        }
      }

      // Handle autocomplete navigation when dropdown is visible
      if (showAutocomplete) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % filteredSuggestions.length);
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelectedIndex(
            (prev) => (prev - 1 + filteredSuggestions.length) % filteredSuggestions.length
          );
          return;
        }
        if (e.key === "Enter") {
          e.preventDefault();
          const selected = filteredSuggestions[selectedIndex];
          if (selected) {
            handleSelectSuggestion(selected);
          } else {
            editableRef.current?.blur();
          }
          return;
        }
        if (e.key === "Tab") {
          e.preventDefault();
          const selected = filteredSuggestions[selectedIndex];
          if (selected) {
            handleSelectSuggestion(selected);
          }
          return;
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        editableRef.current?.blur();
        return;
      }

      // Delete the chip on Backspace when the editable content is empty
      if (e.key === "Backspace") {
        const text = editableRef.current?.textContent || "";
        if (text === "") {
          e.preventDefault();
          setIsEditing(false);
          onDelete();
          return;
        }
      }

      if (e.key === "Escape") {
        e.preventDefault();
        // Revert to original value in DOM
        if (editableRef.current) {
          editableRef.current.textContent = variableId;
        }
        setIsEditing(false);
        setQuery("");
        // If it was a new empty variable, delete it
        if (variableId === "") {
          onDelete();
        }
      }
    },
    [
      variableId,
      onDelete,
      showAutocomplete,
      filteredSuggestions,
      selectedIndex,
      handleSelectSuggestion,
    ]
  );

  const handleInput = useCallback(() => {
    if (editableRef.current) {
      let text = editableRef.current.textContent || "";
      // Enforce max length
      if (text.length > MAX_VARIABLE_LENGTH) {
        text = text.slice(0, MAX_VARIABLE_LENGTH);
        editableRef.current.textContent = text;
        // Move cursor to end
        const range = document.createRange();
        range.selectNodeContents(editableRef.current);
        range.collapse(false);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
      // Update query for autocomplete filtering
      setQuery(text);
      setSelectedIndex(0);
    }
  }, []);

  // Handle paste to strip formatting and enforce max length
  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const text = e.clipboardData.getData("text/plain").slice(0, MAX_VARIABLE_LENGTH);

      // Use modern Range API instead of deprecated document.execCommand
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        const textNode = document.createTextNode(text);
        range.insertNode(textNode);

        // Move cursor to end of inserted text
        range.setStartAfter(textNode);
        range.setEndAfter(textNode);
        selection.removeAllRanges();
        selection.addRange(range);

        // Trigger input event to enforce max length check
        handleInput();
      }
    },
    [handleInput]
  );

  const handleEditTrigger = useCallback(
    (e: React.MouseEvent) => {
      // Stop propagation to prevent TipTap from stealing focus
      e.stopPropagation();
      // Don't allow editing in readonly mode
      if (readOnly) return;
      if (!isEditing) {
        setIsEditing(true);
      }
    },
    [isEditing, readOnly]
  );

  // Truncate display text and prepare title for tooltip
  const displayInfo = useMemo(() => {
    const name = variableId;
    const valueStr = value ? `="${value}"` : "";
    const fullText = `${name}${valueStr}`;
    const isTruncated = name.length > MAX_DISPLAY_LENGTH;
    const displayName = isTruncated ? `${name.slice(0, MAX_DISPLAY_LENGTH)}…` : name;
    const displayText = `${displayName}${valueStr}`;

    return {
      displayText,
      fullText,
      showTitle: isTruncated,
    };
  }, [variableId, value]);

  // Update span content when not editing - show displayText (name + value) instead of just name
  useEffect(() => {
    if (editableRef.current && !isEditing) {
      editableRef.current.textContent = displayInfo.displayText;
    }
  }, [displayInfo.displayText, isEditing]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!isEditing && onSelect) {
        onSelect();
      }
    },
    [isEditing, onSelect]
  );

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const clickProps = singleClickToEdit
    ? { onClick: handleEditTrigger }
    : { onClick: handleClick, onDoubleClick: handleEditTrigger };

  return (
    <>
      <span
        ref={chipRef}
        className={cn(
          "courier-variable-chip",
          !isInvalid && value && "courier-variable-chip-has-value",
          isInvalid && "courier-variable-chip-invalid",
          isSelected && "courier-variable-chip-selected",
          className
        )}
        style={{ direction: "ltr" }}
        onMouseDown={handleMouseDown}
        {...clickProps}
        title={displayInfo.showTitle ? displayInfo.fullText : undefined}
      >
        <span className="courier-flex-shrink-0 courier-flex courier-items-center courier-pt-0.5">
          {icon}
        </span>
        <span
          ref={editableRef}
          role="textbox"
          contentEditable={isEditing}
          suppressContentEditableWarning
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          onPaste={handlePaste}
          onMouseDown={handleMouseDown}
          {...(singleClickToEdit
            ? { onClick: handleEditTrigger }
            : { onClick: handleClick, onDoubleClick: handleEditTrigger })}
          className={cn(
            "courier-outline-none courier-min-w-[1ch]",
            !isEditing && "courier-cursor-text"
          )}
          style={{
            ...(textColorOverride && { color: textColorOverride }),
            ...formattingStyle,
            ...(formattingStyle?.fontStyle === "italic" && { paddingRight: "0.15em" }),
            maxWidth: `var(--courier-variable-chip-max-width, ${MAX_DISPLAY_LENGTH}ch)`,
            overflow: "hidden",
            textOverflow: isEditing ? "clip" : "ellipsis",
            whiteSpace: "nowrap",
            direction: "ltr",
            unicodeBidi: "isolate",
          }}
        >
          {/* Don't render children when editing - let DOM manage contentEditable */}
          {!isEditing && displayInfo.displayText}
        </span>
      </span>

      {/* Autocomplete dropdown - rendered via portal to theme container to preserve theming */}
      {showAutocomplete &&
        createPortal(
          <VariableAutocomplete
            items={filteredSuggestions}
            onSelect={handleSelectSuggestion}
            selectedIndex={selectedIndex}
            anchorRef={chipRef}
          />,
          chipRef.current?.closest(".theme-container") || document.body
        )}
    </>
  );
};
